import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  BookOpen,
  Users,
  ShoppingCart,
  BarChart3,
  Settings,
  Search,
  LogOut,
  Sun,
  Moon,
  Building2,
  MapPin,
  ChevronDown,
  RefreshCw,
  UserSearch,
  Bot,
  Sparkles,
  Trash2,
  Globe,
  Cpu,
  Newspaper,
  CalendarClock,
  DollarSign,
  Package,
  Scale,
  Menu,
  X,
  PieChart,
  Library,
  TrendingUp,
  ShoppingBasket,
  KeyRound,
  MonitorPlay,
  Mail,
  PackageCheck,
  FileUp,
} from "lucide-react";
import { useState, useEffect, useRef, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { BackgroundDecoration, getDecorationVariant } from "./BackgroundDecoration";

interface MainLayoutProps {
  children: ReactNode;
}

const navigation = [
  { name: "Tableau de bord", href: "/", icon: BarChart3, end: true },
  { name: "Catalogue", href: "/catalog", icon: BookOpen },
  { name: "Circulation", href: "/circulation", icon: RefreshCw, end: true },
  { name: "Lecteurs", href: "/circulation/patrons", icon: UserSearch },
  { name: "Réservations", href: "/circulation/reservations", icon: PackageCheck },
];

const acquisitionNavigation = [
  { name: "Fournisseurs", href: "/acquisition/suppliers", icon: Package },
  { name: "Commandes", href: "/acquisition/orders", icon: ShoppingCart },
  { name: "Budgets", href: "/acquisition/budgets", icon: DollarSign },
];

const periodicalNavigation = [
  { name: "Abonnements", href: "/periodical/subscriptions", icon: Newspaper },
  { name: "Bulletinage", href: "/periodical/serial-control", icon: CalendarClock },
];

const aiNavigation = [
  { name: "Copilot", href: "/ai/copilot", icon: Bot },
  { name: "Suggestions IA", href: "/ai/suggestions", icon: Sparkles },
  { name: "Désherbage", href: "/ai/weeding", icon: Trash2 },
];

const interopNavigation = [
  { name: "Recherche externe", href: "/interop/search", icon: Globe },
  { name: "Paniers Electre", href: "/interop/electre-baskets", icon: ShoppingBasket },
];

const statisticsNavigation = [
  { name: "Vue d'ensemble", href: "/statistics", icon: PieChart, end: true },
  { name: "Circulation", href: "/statistics/circulation", icon: RefreshCw },
  { name: "Collections", href: "/statistics/collections", icon: Library },
];

const adminNavigation = [
  { name: "Utilisateurs", href: "/admin/users", icon: Users },
  { name: "Établissements", href: "/admin/locations", icon: Building2 },
  { name: "Catégories et prêts", href: "/admin/loan-rules", icon: Scale },
  { name: "Import de fonds", href: "/import", icon: FileUp },
  { name: "Fournisseurs IA", href: "/admin/ai-providers", icon: Cpu },
  { name: "Electre", href: "/admin/electre", icon: KeyRound },
  { name: "Ressources numériques", href: "/admin/digital-resources", icon: MonitorPlay },
  { name: "Courriels", href: "/admin/mail", icon: Mail },
];

function ThemeToggle() {
  const [isDark, setIsDark] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Check for stored preference or system preference
    const storedTheme = localStorage.getItem("theme");
    const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const shouldBeDark = storedTheme === "dark" || (!storedTheme && systemDark);

    setIsDark(shouldBeDark);
    document.documentElement.classList.toggle("dark", shouldBeDark);
  }, []);

  const applyTheme = (dark: boolean) => {
    setIsDark(dark);
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  };

  const toggleTheme = async () => {
    const newIsDark = !isDark;
    const button = buttonRef.current;

    // Respect reduced motion preference
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Check if View Transitions API is supported (not available in all browsers)
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!document.startViewTransition || prefersReducedMotion || !button) {
      applyTheme(newIsDark);
      return;
    }

    // Get the center of the button as the origin of the circle
    const rect = button.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;

    // Calculate the maximum radius to cover the entire viewport
    const farX = Math.max(cx, window.innerWidth - cx);
    const farY = Math.max(cy, window.innerHeight - cy);
    const maxRadius = Math.hypot(farX, farY);

    // Start the view transition
    const transition = document.startViewTransition(() => {
      applyTheme(newIsDark);
    });

    // Wait for the transition to be ready, then animate the clip-path
    await transition.ready;

    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${String(cx)}px ${String(cy)}px)`,
          `circle(${String(maxRadius)}px at ${String(cx)}px ${String(cy)}px)`,
        ],
      },
      {
        duration: 500,
        easing: "ease-in-out",
        pseudoElement: "::view-transition-new(root)",
      }
    );
  };

  return (
    <Button
      ref={buttonRef}
      variant="ghost"
      size="icon"
      onClick={() => void toggleTheme()}
      aria-label={isDark ? "Activer le thème clair" : "Activer le thème sombre"}
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </Button>
  );
}

export function MainLayout({ children }: MainLayoutProps) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showAcquisitionMenu, setShowAcquisitionMenu] = useState(false);
  const [showPeriodicalMenu, setShowPeriodicalMenu] = useState(false);
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [showInteropMenu, setShowInteropMenu] = useState(false);
  const [showStatisticsMenu, setShowStatisticsMenu] = useState(false);
  // Below the lg breakpoint the sidebar is an off-canvas drawer
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Navigating closes the drawer
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileNavOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [mobileNavOpen]);

  // Get decoration variant based on current route
  const decorationVariant = getDecorationVariant(location.pathname);

  const handleLogout = () => {
    logout();
    void navigate("/login");
  };

  // Get user initials for avatar
  const userInitials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : "??";

  return (
    <div className="flex min-h-screen">
      {/* Skip link for accessibility */}
      <a href="#main-content" className="skip-link">
        Aller au contenu principal
      </a>

      {/* Backdrop of the mobile drawer */}
      {mobileNavOpen && (
        <div
          className="animate-fade-in fixed inset-0 z-30 bg-gray-950/40 backdrop-blur-sm lg:hidden"
          aria-hidden="true"
          onClick={() => {
            setMobileNavOpen(false);
          }}
        />
      )}

      {/* Sidebar: static from lg, off-canvas drawer below (invisible when closed so it
          leaves the tab order and the accessibility tree) */}
      <aside
        id="main-sidebar"
        className={cn(
          "bg-card flex w-64 flex-col border-r",
          "max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-40 max-lg:shadow-2xl max-lg:transition-[transform,visibility] max-lg:duration-200",
          mobileNavOpen ? "max-lg:translate-x-0" : "max-lg:invisible max-lg:-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center gap-3 border-b px-6">
          <BookOpen className="text-primary h-8 w-8" aria-hidden="true" />
          <span className="text-xl font-semibold">Lumen</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-4" aria-label="Navigation principale">
          {/* Main navigation */}
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.end ?? false}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
            >
              <item.icon className="h-5 w-5" aria-hidden="true" />
              {item.name}
            </NavLink>
          ))}

          {/* Statistics Section */}
          <div className="pt-2">
            <button
              onClick={() => {
                setShowStatisticsMenu(!showStatisticsMenu);
              }}
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            >
              <TrendingUp className="h-5 w-5" aria-hidden="true" />
              Statistiques
              <ChevronDown
                className={cn(
                  "ml-auto h-4 w-4 transition-transform",
                  showStatisticsMenu && "rotate-180"
                )}
              />
            </button>
            {showStatisticsMenu && (
              <div className="mt-1 ml-4 space-y-1 border-l pl-4">
                {statisticsNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    end={item.end ?? false}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      )
                    }
                  >
                    <item.icon className="h-4 w-4" aria-hidden="true" />
                    {item.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* AI Section */}
          <div className="pt-2">
            <button
              onClick={() => {
                setShowAIMenu(!showAIMenu);
              }}
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            >
              <Sparkles className="h-5 w-5" aria-hidden="true" />
              Intelligence IA
              <ChevronDown
                className={cn("ml-auto h-4 w-4 transition-transform", showAIMenu && "rotate-180")}
              />
            </button>
            {showAIMenu && (
              <div className="mt-1 ml-4 space-y-1 border-l pl-4">
                {aiNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      )
                    }
                  >
                    <item.icon className="h-4 w-4" aria-hidden="true" />
                    {item.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* Acquisitions Section */}
          <div className="pt-2">
            <button
              onClick={() => {
                setShowAcquisitionMenu(!showAcquisitionMenu);
              }}
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            >
              <ShoppingCart className="h-5 w-5" aria-hidden="true" />
              Acquisitions
              <ChevronDown
                className={cn(
                  "ml-auto h-4 w-4 transition-transform",
                  showAcquisitionMenu && "rotate-180"
                )}
              />
            </button>
            {showAcquisitionMenu && (
              <div className="mt-1 ml-4 space-y-1 border-l pl-4">
                {acquisitionNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      )
                    }
                  >
                    <item.icon className="h-4 w-4" aria-hidden="true" />
                    {item.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* Periodicals Section */}
          <div className="pt-2">
            <button
              onClick={() => {
                setShowPeriodicalMenu(!showPeriodicalMenu);
              }}
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            >
              <Newspaper className="h-5 w-5" aria-hidden="true" />
              Périodiques
              <ChevronDown
                className={cn(
                  "ml-auto h-4 w-4 transition-transform",
                  showPeriodicalMenu && "rotate-180"
                )}
              />
            </button>
            {showPeriodicalMenu && (
              <div className="mt-1 ml-4 space-y-1 border-l pl-4">
                {periodicalNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      )
                    }
                  >
                    <item.icon className="h-4 w-4" aria-hidden="true" />
                    {item.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* Interoperability Section */}
          <div className="pt-2">
            <button
              onClick={() => {
                setShowInteropMenu(!showInteropMenu);
              }}
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            >
              <Globe className="h-5 w-5" aria-hidden="true" />
              Interopérabilité
              <ChevronDown
                className={cn(
                  "ml-auto h-4 w-4 transition-transform",
                  showInteropMenu && "rotate-180"
                )}
              />
            </button>
            {showInteropMenu && (
              <div className="mt-1 ml-4 space-y-1 border-l pl-4">
                {interopNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      )
                    }
                  >
                    <item.icon className="h-4 w-4" aria-hidden="true" />
                    {item.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {/* Admin section */}
          {isAdmin && (
            <div className="pt-2">
              <button
                onClick={() => {
                  setShowAdminMenu(!showAdminMenu);
                }}
                className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
              >
                <Settings className="h-5 w-5" aria-hidden="true" />
                Administration
                <ChevronDown
                  className={cn(
                    "ml-auto h-4 w-4 transition-transform",
                    showAdminMenu && "rotate-180"
                  )}
                />
              </button>
              {showAdminMenu && (
                <div className="mt-1 ml-4 space-y-1 border-l pl-4">
                  {adminNavigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        )
                      }
                    >
                      <item.icon className="h-4 w-4" aria-hidden="true" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* User section */}
        <div className="border-t p-4">
          {user?.location && (
            <div className="text-muted-foreground mb-3 flex items-center gap-2 text-xs">
              <MapPin className="h-3 w-3" />
              <span className="truncate">{user.location.name}</span>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="bg-primary text-primary-foreground flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium">
              {userInitials}
            </div>
            <div className="flex-1 truncate">
              <p className="text-sm font-medium">{user?.fullName}</p>
              <p className="text-muted-foreground truncate text-xs">{user?.email}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="Déconnexion">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="relative flex min-w-0 flex-1 flex-col">
        {/* Background decoration - covers entire content area except sidebar */}
        <BackgroundDecoration variant={decorationVariant} />

        {/* Header */}
        <header className="bg-card/80 relative z-10 flex h-16 items-center justify-between gap-2 border-b px-4 backdrop-blur-sm sm:px-6">
          <Button
            ref={menuButtonRef}
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label={mobileNavOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={mobileNavOpen}
            aria-controls="main-sidebar"
            onClick={() => {
              setMobileNavOpen((open) => !open);
            }}
          >
            {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>

          {/* Search */}
          <div className="flex flex-1 items-center gap-2">
            <div className="relative w-full max-w-md">
              <Search
                className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
                aria-hidden="true"
              />
              <input
                type="search"
                placeholder="Rechercher..."
                className="bg-background/80 placeholder:text-muted-foreground focus-visible:ring-ring h-10 w-full rounded-lg border pr-4 pl-10 text-sm backdrop-blur-sm focus-visible:ring-2 focus-visible:outline-none"
                aria-label="Recherche globale"
                disabled
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </header>

        {/* Page content */}
        <main
          id="main-content"
          className="relative z-10 min-w-0 flex-1 overflow-auto p-4 sm:p-6"
          tabIndex={-1}
        >
          {/* Actual page content */}
          <div className="relative">{children}</div>
        </main>
      </div>
    </div>
  );
}
